import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Code, Send, Copy, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const APIPlayground = ({ reducedMotion }) => {
  const [endpoint, setEndpoint] = useState('/api/analytics/kpis');
  const [method, setMethod] = useState('GET');
  const [body, setBody] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const endpoints = [
    { value: '/api/analytics/kpis', label: 'Dashboard KPIs', method: 'GET' },
    { value: '/api/analytics/hourly-heatmap', label: 'Hourly Heatmap', method: 'GET' },
    { value: '/api/analytics/call-funnel', label: 'Call Funnel', method: 'GET' },
    { value: '/api/analytics/asr-performance', label: 'ASR Performance', method: 'GET' },
    { value: '/api/campaigns', label: 'List Campaigns', method: 'GET' },
    { value: '/api/calls', label: 'List Calls', method: 'GET' },
    { value: '/api/insights/v2/forecast', label: 'Trend Forecast', method: 'GET' },
    { value: '/api/insights/v2/anomalies', label: 'Anomaly Prediction', method: 'GET' }
  ];

  const handleSendRequest = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const url = `${backendUrl}${endpoint}`;
      
      const options = {
        method,
        headers: { 'Content-Type': 'application/json' }
      };
      
      if (method !== 'GET' && body) {
        options.body = body;
      }

      const res = await fetch(url, options);
      const data = await res.json();
      
      setResponse({
        status: res.status,
        statusText: res.ok ? 'OK' : 'Error',
        data: JSON.stringify(data, null, 2)
      });
      
      toast.success('Request completed successfully');
    } catch (error) {
      setResponse({
        status: 500,
        statusText: 'Error',
        data: JSON.stringify({ error: error.message }, null, 2)
      });
      toast.error('Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyResponse = () => {
    if (response && response.data) {
      navigator.clipboard.writeText(response.data);
      setCopied(true);
      toast.success('Response copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="api-playground-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
          <Code className="w-5 h-5 text-pink-400" />
          API Playground
        </CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          Test and explore analytics API endpoints interactively
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Request Builder */}
          <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
            <h4 className="text-sm font-medium text-slate-300 mb-3">Request</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <select
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                className="col-span-2 px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-slate-100"
                data-testid="endpoint-select"
              >
                {endpoints.map(ep => (
                  <option key={ep.value} value={ep.value}>{ep.label} ({ep.method})</option>
                ))}
              </select>
              
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-slate-100"
                data-testid="method-select"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>

            {method !== 'GET' && (
              <div className="mb-3">
                <label className="text-xs text-slate-400 mb-2 block">Request Body (JSON)</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder='{"key": "value"}'
                  rows={4}
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-slate-100 font-mono placeholder-slate-500"
                  data-testid="request-body-input"
                />
              </div>
            )}

            <Button
              onClick={handleSendRequest}
              disabled={loading}
              className="bg-pink-600 hover:bg-pink-700 text-white w-full"
              data-testid="send-request-button"
            >
              {loading ? (
                <span className="animate-pulse">Sending...</span>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Request
                </>
              )}
            </Button>
          </div>

          {/* Response */}
          {response && (
            <div 
              className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50"
              style={{
                animation: reducedMotion ? 'none' : 'fadeIn 0.3s ease-out'
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium text-slate-300">Response</h4>
                  <Badge
                    className={response.status < 300 ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}
                    data-testid="response-status-badge"
                  >
                    {response.status} {response.statusText}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyResponse}
                  className="text-slate-400 hover:text-slate-100"
                  data-testid="copy-response-button"
                >
                  {copied ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              
              <div className="relative">
                <pre className="bg-slate-900/70 p-4 rounded-lg text-xs text-slate-300 font-mono overflow-x-auto max-h-96 overflow-y-auto" data-testid="response-body">
                  {response.data}
                </pre>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default APIPlayground;